import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Star, CheckCircle2, Circle, X, LifeBuoy } from "lucide-react";
import { CATEGORY_ICONS, ContactSupportDialog, plain } from "@/components/help/helpShared";
import { AskTopRecruit, PopularQuestions } from "@/components/help/AskTopRecruit";
import { toast } from "sonner";
import { StaffFaqSection } from "@/components/help/StaffFaqSection";

function Card({ a, onClick, labels }) {
  const aud = (a.audience || []).filter((k) => k !== "everyone");
  return (
    <button data-testid={`help-article-card-${a.slug}`} onClick={onClick}
      className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-brand/40 hover:shadow-md transition-all group min-w-0">
      <p className="font-semibold text-slate-800 text-sm group-hover:text-brand">{a.title}</p>
      {a.summary && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{plain(a.summary)}</p>}
      {labels && <p className="text-[11px] text-slate-400 mt-1.5" data-testid={`help-card-meta-${a.slug}`}>{labels.category_labels?.[a.category]}{aud.length ? ` · For ${aud.map((k) => labels.audience_labels?.[k] || k).join(", ")}` : ""}</p>}
    </button>
  );
}

function Checklist() {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get("/help/progress").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data || data.dismissed || !data.items?.length) return null;
  const done = new Set(data.done || []);
  const complete = data.items.filter((i) => done.has(i.key)).length;
  const toggle = (key) => api.post("/help/progress", { action: "complete_item", key }).then(load).catch(() => {});
  const dismiss = () => api.post("/help/progress", { action: "dismiss_checklist" }).then(load).catch(() => {});
  return (
    <div className="rounded-xl border border-brand/20 bg-brand/5 p-5 mb-6" data-testid="onboarding-checklist">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-semibold text-slate-900">Getting set up</p>
          <p className="text-xs text-slate-500">{complete} of {data.items.length} complete</p>
        </div>
        <button onClick={dismiss} data-testid="checklist-dismiss" className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-1.5">
        {data.items.map((i) => (
          <button key={i.key} onClick={() => toggle(i.key)} data-testid={`checklist-item-${i.key}`}
            className="flex items-center gap-2.5 w-full text-left text-sm py-1 hover:text-brand">
            {done.has(i.key) ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
            <span className={done.has(i.key) ? "line-through text-slate-400" : "text-slate-700"}>{i.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function HelpCenter({ onOpenArticle, onSubmitTicket } = {}) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const ask = sp.get("ask") || "";
  const page = sp.get("from") || "";
  const category = sp.get("category") || "";
  const showAllRoles = sp.get("all") === "1";
  const [query, setQuery] = useState(q || ask);
  const [home, setHome] = useState(null);
  const [results, setResults] = useState(null);
  const [support, setSupport] = useState(false);

  useEffect(() => { api.get("/help/home").then((r) => setHome(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  useEffect(() => {
    if (!q && !category) { setResults(null); return; }
    if (category === "glossary") { api.get("/help/glossary").then((r) => setResults({ glossary: r.data.terms, count: r.data.terms.length })).catch(() => {}); return; }
    api.get("/help/articles", { params: { q, category, ...(showAllRoles ? { all: true } : {}) } }).then((r) => setResults(r.data)).catch(() => {});
  }, [q, category, showAllRoles]);

  const runSearch = (e) => { e?.preventDefault(); setSp(query.trim() ? { ask: query.trim(), ...(page ? { from: page } : {}) } : {}); };
  const askQ = (text) => { setQuery(text); setSp({ ask: text }); };
  const showAll = () => setSp({ q: ask || query });
  const openArticle = (slug) => onOpenArticle ? onOpenArticle(slug) : navigate(`/help/article/${slug}`);
  const submitTicket = (pre) => {
    if (onSubmitTicket) return onSubmitTicket(pre);
    const p = new URLSearchParams({ new: "1", ...(pre?.subject ? { subject: pre.subject, description: pre.description || "" } : {}) });
    navigate(`/support/my-tickets?${p.toString()}`);
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="help-center">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-brand to-brand-strong text-white p-8 mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-bold">How can we help?</h1>
        <p className="text-white/80 mt-1 text-sm">Ask in plain English. Answers come from Top-Recruit documentation you're allowed to see.</p>
        <form onSubmit={runSearch} className="mt-5 relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input data-testid="help-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask a question about Top-Recruit..." className="pl-10 h-12 bg-white text-slate-800 border-0" />
        </form>
      </div>

      {ask && !q && !category && <AskTopRecruit question={ask} page={page} onOpenArticle={openArticle} onTicket={submitTicket} onShowAll={showAll} />}

      {ask && !q && !category ? null : results ? (
        <ResultsView results={results} q={q} category={category} home={home} showAllRoles={showAllRoles} setSp={setSp}
          onClear={() => { setQuery(""); setSp({}); }} openArticle={openArticle} submitTicket={submitTicket} />
      ) : !home ? (
        <div className="h-64 animate-pulse rounded-xl bg-slate-50" />
      ) : (
        <>
          <Checklist />
          <PopularQuestions onAsk={askQ} />

          {home.recommended?.length > 0 && (
            <Section title="Recommended for you" testId="help-recommended" items={home.recommended} onClick={openArticle} labels={home} />
          )}

          <StaffFaqSection onOpen={openArticle} autoAdd={sp.get("add_faq") === "1"} onAutoAddDone={() => setSp({})} />

          <h2 className="font-semibold text-slate-900 mb-3">Browse by topic</h2>
          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-3">
            {[...home.categories, ...(home.glossary_count ? [{ key: "glossary", label: "Glossary", icon: "BookOpen", count: home.glossary_count }] : [])].map((c) => {
              const Icon = CATEGORY_ICONS[c.icon] || CATEGORY_ICONS.HelpCircle;
              return (
                <button key={c.key} data-testid={`help-category-${c.key}`} onClick={() => setSp({ category: c.key })}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-brand/40 hover:shadow-md transition-all group min-w-0">
                  <span className="w-10 h-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Icon className="w-5 h-5" /></span>
                  <div className="min-w-0 text-left"><p className="font-medium text-slate-800 text-sm truncate">{c.label}</p><p className="text-xs text-slate-400">{c.count} {c.key === "glossary" ? "term" : "article"}{c.count === 1 ? "" : "s"}</p></div>
                </button>
              );
            })}
          </div>
          {home.other_count > 0 && <p className="text-xs text-slate-500 mb-8" data-testid="help-other-roles-note">Showing help written for you. {home.other_count} more article{home.other_count === 1 ? " is" : "s are"} written for other roles — search finds them, or open a topic and choose “Include other roles”.</p>}

          {home.popular?.length > 0 && (
            <Section title="Popular help" icon={Star} testId="help-popular" items={home.popular} onClick={openArticle} labels={home} />
          )}

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center mt-2" data-testid="help-still-need-help">
            <p className="font-semibold text-slate-800">Still need help?</p>
            <p className="text-sm text-slate-500 mb-3">Our team is here for you.</p>
            <Button onClick={() => setSupport(true)} className="bg-brand hover:bg-brand-strong text-white" data-testid="help-contact-support"><LifeBuoy className="w-4 h-4 mr-1.5" />Contact Support</Button>
          </div>
        </>
      )}
      <ContactSupportDialog open={support} onClose={() => setSupport(false)} />
    </div>
  );
}

function Section({ title, icon: Icon, items, onClick, testId, labels }) {
  return (
    <div className="mb-8" data-testid={testId}>
      <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-slate-400" />}{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{items.map((a) => <Card key={a.slug} a={a} onClick={() => onClick(a.slug)} labels={labels} />)}</div>
    </div>
  );
}

function ResultsView({ results, q, category, home, showAllRoles, setSp, onClear, openArticle, submitTicket }) {
  const label = home?.category_labels?.[category] || category;
  const grouped = category && results.items ? results.items.reduce((m, a) => ({ ...m, [a.topic || "General"]: [...(m[a.topic || "General"] || []), a] }), {}) : null;
  const [term, setTerm] = useState("");
  return (
    <div data-testid="help-search-results">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-semibold text-slate-900">{category ? label : `Results for "${q}"`} · {results.count}</h2>
        <div className="flex items-center gap-2">
          {category && category !== "glossary" && <label className="text-xs text-slate-600 flex items-center gap-1.5"><input type="checkbox" checked={showAllRoles} onChange={(e) => setSp(e.target.checked ? { category, all: "1" } : { category })} data-testid="help-include-other-roles" />Include other roles</label>}
          <Button variant="outline" size="sm" onClick={onClear} data-testid="help-clear-search">Back</Button>
        </div>
      </div>
      {results.glossary ? (
        <div data-testid="help-glossary">
          <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Find a term…" className="mb-3 max-w-sm" data-testid="help-glossary-search" />
          <dl className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {results.glossary.filter((t) => !term || t.term.toLowerCase().includes(term.toLowerCase())).map((t) => (
              <div key={t.slug} className="p-3" data-testid={`help-term-${t.slug}`}><dt className="font-medium text-sm text-slate-800">{t.term}</dt><dd className="text-sm text-slate-600 mt-0.5">{plain(t.definition || "")}</dd></div>))}
          </dl>
        </div>
      ) : results.count === 0 ? (
        <div className="text-center py-12 text-slate-400" data-testid="help-no-results">
          <p>No help articles matched your search.</p>
          <Button variant="outline" className="mt-3" onClick={() => submitTicket()} data-testid="help-no-results-support"><LifeBuoy className="w-4 h-4 mr-1.5" />Submit a Support Ticket</Button>
        </div>
      ) : grouped ? Object.entries(grouped).map(([topic, list]) => (
        <section key={topic} className="mb-6" data-testid={`help-topic-${topic.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {Object.keys(grouped).length > 1 && <h3 className="text-sm font-semibold text-slate-700 mb-2">{topic}</h3>}
          <div className="grid sm:grid-cols-2 gap-3">{list.map((a) => <Card key={a.slug} a={a} onClick={() => openArticle(a.slug)} labels={home} />)}</div>
        </section>))
      : <div className="grid sm:grid-cols-2 gap-3">{results.items.map((a) => <Card key={a.slug} a={a} onClick={() => openArticle(a.slug)} labels={home} />)}</div>}
    </div>
  );
}
